import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { Platform } from 'react-native';

import {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID,
} from '@env';
import { useEmulator, env } from '@/config/env';
import { USE_API } from '@/services/api/client';

// En modo API (Oracle/Postgres) NO usamos Firebase para nada: datos y auth van
// 100% por la API REST. Inicializamos Firebase con una config inerte (proyecto
// dummy, sin persistencia, sin emulador) para que el SDK NUNCA contacte a
// firestore/identitytoolkit.googleapis.com — así ningún adblocker rompe la app.
// OJO: usamos el TLD reservado .invalid (RFC 6761), nunca .local — .local es
// red local (mDNS) y dispara el permiso "acceder a dispositivos de tu red".
const firebaseConfig = USE_API
  ? {
      apiKey: 'api-mode-no-firebase',
      authDomain: 'api-mode.invalid',
      projectId: 'clinicat-api-mode',
      storageBucket: 'api-mode.invalid',
      messagingSenderId: '0',
      appId: 'api-mode',
    }
  : {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  ...(FIREBASE_MEASUREMENT_ID ? { measurementId: FIREBASE_MEASUREMENT_ID } : {}),
};

function assertConfig() {
  const missing = Object.entries({
    FIREBASE_API_KEY,
    FIREBASE_AUTH_DOMAIN,
    FIREBASE_PROJECT_ID,
    FIREBASE_STORAGE_BUCKET,
    FIREBASE_MESSAGING_SENDER_ID,
    FIREBASE_APP_ID,
  })
    .filter(([, value]) => !value || value === '__COMPLETAR__')
    .map(([key]) => key);

  if (missing.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[CliniCAT] Faltan variables de entorno de Firebase: ${missing.join(', ')}. ` +
        `Completalas en .env antes de iniciar la app.`,
    );
  }
}
if (!USE_API) assertConfig();

export const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const firestore: Firestore = initializeFirestore(app, {
  // Firestore rechaza campos con valor undefined; los ignoramos para que
  // los upsert (que pueden traer id undefined al crear) no fallen.
  ignoreUndefinedProperties: true,
  // En modo API no usamos Firestore: cache en memoria, sin persistencia ni red.
  ...(USE_API
    ? {}
    : {
        localCache:
          Platform.OS === 'web'
            ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
            : persistentLocalCache({}),
      }),
});

export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);

if (USE_API) {
  // eslint-disable-next-line no-console
  console.info('[CliniCAT] Backend: API REST (Postgres) · Firebase deshabilitado');
} else if (useEmulator) {
  const host = Platform.OS === 'android' ? '10.0.2.2' : 'localhost';
  try {
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(firestore, host, 8080);
    connectStorageEmulator(storage, host, 9199);
    // eslint-disable-next-line no-console
    console.info(`[CliniCAT] Ambiente: ${env} · Emuladores en ${host}`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[CliniCAT] No se pudieron conectar emuladores:', e);
  }
} else {
  // eslint-disable-next-line no-console
  console.info(`[CliniCAT] Ambiente: ${env} · Firebase real (${FIREBASE_PROJECT_ID})`);
}
